import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowUpRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { StarRating } from "./StarRating";
import { money } from "../lib/api";

const GOAL_LABEL = {
  recovery: "Recover",
  lean: "Get Lean",
  performance: "Perform",
  essentials: "Essential",
};

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const ref = useRef(null);

  const onMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  const discount =
    product.compare_at && product.compare_at > product.price
      ? Math.round((1 - product.price / product.compare_at) * 100)
      : null;

  const highlighted = product.in_stock && (product.is_new || product.is_bestseller);

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      className={`spotlight-card group relative bg-ink-800 transition-all duration-300 flex flex-col ${
        highlighted
          ? "border-2 border-accent shadow-[0_0_36px_rgba(255,51,31,0.22)]"
          : "border border-[#27272A] hover:border-accent/60"
      } ${!product.in_stock ? "opacity-90" : ""}`}
      data-testid={`product-card-${product.handle}`}
    >
      <Link to={`/product/${product.handle}`} className="block relative overflow-hidden clip-corner">
        <div className="aspect-square bg-ink-700 relative">
          <img
            src={product.images?.[0]}
            alt={product.name}
            className={`h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${!product.in_stock ? "grayscale brightness-[0.35]" : ""}`}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-900/70 via-transparent to-transparent" />
          {product.is_new && product.in_stock && (
            <span className="absolute top-3 left-3 bg-white text-ink-900 font-display font-black text-xs tracking-widest px-3 py-1.5 shadow-[0_0_24px_rgba(255,255,255,0.45)] animate-pulse" data-testid={`new-badge-${product.handle}`}>
              NEW
            </span>
          )}
          {product.is_bestseller && product.in_stock && !product.is_new && (
            <span className="absolute top-3 left-3 bg-accent text-white font-display font-black text-xs tracking-widest px-3 py-1.5 shadow-[0_0_24px_rgba(255,51,31,0.55)]" data-testid={`bestseller-badge-${product.handle}`}>
              BEST SELLER
            </span>
          )}
          {discount && product.in_stock && (
            <span className={`absolute ${highlighted ? "top-11" : "top-3"} left-3 bg-accent text-white font-mono-tech text-[10px] font-bold tracking-widest px-2 py-1`}>
              -{discount}%
            </span>
          )}
          {!product.in_stock && (
            <div className="absolute inset-0 flex items-center justify-center" data-testid={`sold-out-${product.handle}`}>
              <span className="font-display font-black text-2xl tracking-[0.2em] uppercase text-accent border-2 border-accent bg-ink-900/80 backdrop-blur-sm px-6 py-3 -rotate-12 shadow-[0_0_40px_rgba(255,51,31,0.35)]">
                Sold Out
              </span>
            </div>
          )}
          <span className="absolute top-3 right-3 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-zinc-300 bg-ink-900/60 backdrop-blur px-2 py-1 border border-white/10">
            {GOAL_LABEL[product.goals?.[0]] || "Research"}
          </span>
        </div>
      </Link>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <Link to={`/product/${product.handle}`}>
              <h3 className="font-display font-extrabold text-xl tracking-tight leading-none group-hover:text-accent transition-colors">
                {product.name}
              </h3>
            </Link>
            <p className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-zinc-500 mt-1">
              {(product.variants || []).length > 1 ? `${product.variants.length} size options` : product.dose}
            </p>
          </div>
          {product.rating && (
            <div className="flex flex-col items-end">
              <StarRating rating={product.rating} size={12} />
              <span className="font-mono-tech text-[10px] text-zinc-500 mt-1">
                {product.rating} · {product.review_count}
              </span>
            </div>
          )}
        </div>

        <p className="text-sm text-zinc-400 mt-3 line-clamp-2 leading-relaxed flex-1">
          {product.summary}
        </p>

        <div className="flex items-end justify-between mt-5 pt-4 border-t border-[#27272A]">
          <div className="flex items-baseline gap-2">
            <span className="font-display font-extrabold text-2xl">
              {(product.variants || []).length > 1 && (
                <span className="font-mono-tech text-[10px] text-zinc-500 uppercase tracking-widest mr-1.5">from</span>
              )}
              {money(product.price)}
            </span>
            {product.compare_at && (
              <span className="font-mono-tech text-xs text-zinc-600 line-through">
                {money(product.compare_at)}
              </span>
            )}
          </div>
          {product.in_stock ? (
            <button
              onClick={() => addItem(product, 1)}
              className="flex items-center gap-1.5 bg-white text-ink-900 hover:bg-accent hover:text-white font-display font-bold text-sm uppercase tracking-wide px-3.5 py-2.5 transition-colors"
              data-testid={`quick-add-${product.handle}`}
            >
              <Plus size={15} /> Add
            </button>
          ) : (
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 px-2 py-2.5">
              Back soon
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
